import i18n from "../language/i18n";
import {
  CategoryLookup,
  personalizedAssignmentThemeNameOf,
} from "./CategoryThemeUtilization";

/**
 * The staff-sent personalized-assignment triggers: BRANCH_ASSIGNMENT (hung on a şube ödevi) and
 * STAFF_DIRECT (sent straight from the console's create wizard, hung on nothing). Both reach the
 * learner through Ödevlerim — they count into its badge, list among the active homeworks, and exit
 * back to /homeworks — unlike the learner-initiated triggers (SUBJECT_DESCRIPTION, LOW_SCORE_MIX,
 * READINESS), which live on the learning path.
 *
 * One predicate instead of three scattered === "BRANCH_ASSIGNMENT" checks, so the next staff
 * trigger cannot be invisible in one surface and present in another.
 */
export const isStaffSentPersonalizedAssignment = (
  triggerType?: string | null,
): boolean =>
  triggerType === "BRANCH_ASSIGNMENT" || triggerType === "STAFF_DIRECT";

/**
 * The display name of a STAFF_DIRECT round, which hangs on no parent şube ödevi to borrow a name
 * from: the THEME of its heaviest outcome, else its ders (carried on the row itself). Undefined for
 * every other trigger, and for a row that resolves neither.
 */
export const directPersonalizedAssignmentName = (
  personalizedAssignment: any,
  categoryLookup?: CategoryLookup,
): string | undefined => {
  if (personalizedAssignment?.triggerType !== "STAFF_DIRECT") return undefined;
  const themeName = categoryLookup
    ? personalizedAssignmentThemeNameOf(personalizedAssignment, categoryLookup)
    : undefined;
  if (themeName) return themeName;
  return personalizedAssignment?.coterieType
    ? i18n.t(personalizedAssignment.coterieType)
    : undefined;
};

/**
 * The topic line of a bireysel ödev's homework card, under the ders that leads the row. A
 * şube-ödevi-hung round borrows its parent's name; a direct round names its outcome's THEME. Never
 * the kind noun ("Bireysel ödev") — the card's kind eyebrow already says it, and repeating it under
 * a second wording is what this replaced. Nor the ders, which the line above already shows: an
 * unresolvable theme leaves the line empty.
 */
export const personalizedAssignmentCardTopicOf = (
  personalizedAssignment: any,
  categoryLookup: CategoryLookup,
): string => {
  // Asked of the trigger, not by comparing the carried name to the ders: a şube ödevi named after
  // its lesson ("Matematik") is common, and that comparison would throw away a real parent name.
  const carriedName = personalizedAssignment?.subjectDescriptionDTO?.name;
  if (carriedName && personalizedAssignment?.triggerType !== "STAFF_DIRECT") {
    return carriedName;
  }
  return personalizedAssignmentThemeNameOf(personalizedAssignment, categoryLookup) ?? "";
};
